import { Box, Button, Checkbox, Chip, CircularProgress, Typography } from '@mui/material';
import { AlertType, AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import {
  BdmBatchFileStatus,
  bdmBatchFileStatuses,
  bdmBatchFileStatusMap,
} from 'src/app/modules/OpmPd/constants/common.constant';
import {
  canSubmitBdmBatchFilePolicy,
  canViewEndedBdmBatchFilePolicy,
} from 'src/app/modules/OpmPd/helpers/common.helper';
import { formatDateTime, formatNumber } from 'src/app/modules/OpmPd/utils/commonHelper';
import { useBatchFilesListPage } from '../../useBatchFilesPage';
import { BatchFileListContext } from './BatchFileList.context';
import UploadFile from './components/UploadFile';
import AppTable from 'src/app/common/components/AppTable/AppTable';
import {
  exportBdmBatchFileSubmittedPolicies,
  exportBdmBatchFileFinalPolicies,
} from 'src/app/modules/OpmPd/network/opm-pd-bdm';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import _isEqual from 'lodash/isEqual';
import { appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { RootState } from 'src/redux/store';

const initialFilters = {
  batchStatus: '',
  submittedAtFrom: '',
  submittedAtTo: '',
};
const SELECTED_BATCH_IDS_LIMIT = 20;

type PolicyExportProps = {
  batchIds: string[];
  onExportSuccess?: () => void;
};

const PolicyExport = (props: PolicyExportProps) => {
  const { batchIds, onExportSuccess } = props;
  const [isExportingSubmittedPolicies, setIsExportingSubmittedPolicies] = React.useState(false);
  const [isExportingFinalPolicies, setIsExportingFinalPolicies] = React.useState(false);

  const t = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);

  const handlePolicyExport = (module: 'submittedPolicies' | 'finalPolicies') => async () => {
    try {
      if (module === 'submittedPolicies') {
        setIsExportingSubmittedPolicies(true);
        await exportBdmBatchFileSubmittedPolicies({ batchIds }, dispatch);
      } else {
        setIsExportingFinalPolicies(true);
        await exportBdmBatchFileFinalPolicies({ batchIds }, dispatch);
      }
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Yêu cầu xuất báo cáo đã được ghi nhận. File báo cáo sẽ được gửi đến Email ${user?.sub}`,
          },
        ]),
      );
      onExportSuccess?.();
    } catch (_) {}
    setIsExportingSubmittedPolicies(false);
    setIsExportingFinalPolicies(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handlePolicyExport('submittedPolicies')}
        disabled={isExportingFinalPolicies || isExportingSubmittedPolicies || batchIds.length === 0}
        startIcon={isExportingSubmittedPolicies ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {t('opm_pd.submittedExport')}
      </Button>
      <Button
        variant="contained"
        onClick={handlePolicyExport('finalPolicies')}
        disabled={isExportingFinalPolicies || isExportingSubmittedPolicies || batchIds.length === 0}
        startIcon={isExportingFinalPolicies ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {t('opm_pd.finalExport')}
      </Button>
    </>
  );
};

const BatchFileList: FC = () => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  const { filterState, batchFilesList, isLoading, setFilterState, refreshData, onSort } = useBatchFilesListPage({
    initialParams: initialFilters,
  });

  const selectableBatchIds = useMemo(() => {
    return (
      batchFilesList?.items
        .filter((batchFile) => batchFile.batchStatus === BdmBatchFileStatus.SUBMITTED)
        .map((batchFile) => String(batchFile.id)) || []
    );
  }, [batchFilesList]);

  const selectedCountOnPage = selectableBatchIds.filter((id) => selectedBatchIds.includes(id)).length;
  const isSelectedBatchIdsLimitReached = selectedCountOnPage >= SELECTED_BATCH_IDS_LIMIT;
  const isAllSelectedOnPage =
    selectableBatchIds.length > 0 &&
    (selectedCountOnPage === selectableBatchIds.length || isSelectedBatchIdsLimitReached);
  const isIndeterminateOnPage = selectedCountOnPage > 0 && !isAllSelectedOnPage;

  const handleSelectAllOnPage = (checked: boolean) => {
    setSelectedBatchIds((prev) => {
      if (checked) {
        const remainingSlots = SELECTED_BATCH_IDS_LIMIT - prev.length;
        const batchIdsToSelect = selectableBatchIds.filter((id) => !prev.includes(id)).slice(0, remainingSlots);

        return [...prev, ...batchIdsToSelect];
      }

      const batchIdSet = new Set(selectableBatchIds);
      return prev.filter((id) => !batchIdSet.has(id));
    });
  };

  const handleToggleBatch = (batchId: string) => {
    setSelectedBatchIds((prev) => {
      if (prev.includes(batchId)) {
        return prev.filter((id) => id !== batchId);
      }

      return [...prev, batchId];
    });
  };

  const batchFileIds = useMemo(() => {
    return batchFilesList?.items?.map((batchFile) => String(batchFile.id)) || [];
  }, [batchFilesList?.items]);
  const batchFileIdsRef = useRef(batchFileIds);

  useEffect(() => {
    if (_isEqual(batchFileIdsRef.current, batchFileIds)) {
      return;
    }
    setSelectedBatchIds([]);
    batchFileIdsRef.current = batchFileIds;
  }, [batchFileIds]);

  return (
    <BatchFileListContext.Provider value={{ refreshData }}>
      <PruFilter
        title={Translation('opm_pd.filter')}
        itemDef={[
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'batchStatus',
            initialValue: '',
            displayName: Translation('opm_pd.status'),
            choices: [
              {
                displayName: Translation('opm_pd.all'),
                value: '',
              },
              ...bdmBatchFileStatuses.map((status) => ({
                displayName: Translation(status.label),
                value: status.code,
              })),
            ],
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'submittedAtFrom',
            fieldTo: 'submittedAtTo',
            displayName: Translation('opm_pd.submittedAt'),
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({ ...filterState, ...data, page: 1 });
        }}
        fetchData={refreshData}
      />
      <AppTable
        title={`${Translation('opm_pd.batchFileList')} ${
          selectedBatchIds.length
            ? `(${intl.formatMessage({ id: 'opm_pd.batchSelected' }, { count: selectedBatchIds.length })})`
            : ''
        }`}
        disableBulkSelect
        operationDef={[]}
        startHeaderActions={
          <>
            <PolicyExport batchIds={selectedBatchIds} onExportSuccess={() => setSelectedBatchIds([])} />
            <UploadFile />
          </>
        }
        columnDef={[
          // Add columns here
          {
            isId: true,
            hidden: true,
            keyIndex: 'id',
            displayName: '',
            renderData: () => '',
            sortable: true,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'select',
            displayName: (
              <>
                <Checkbox
                  checked={isAllSelectedOnPage}
                  indeterminate={isIndeterminateOnPage}
                  onChange={(event) => handleSelectAllOnPage(event.target.checked)}
                  sx={{
                    color: 'white',
                    '&.Mui-checked': { color: 'white' },
                    '&.MuiCheckbox-indeterminate': { color: 'white' },
                  }}
                />
              </>
            ),
            renderData: (row) => {
              const batchId = String(row.id ?? '');
              const checked = selectedBatchIds.includes(batchId);
              const disabled =
                !selectableBatchIds.includes(batchId) ||
                (!checked && selectedBatchIds.length >= SELECTED_BATCH_IDS_LIMIT);

              return (
                <>
                  <Checkbox checked={checked} onClick={() => handleToggleBatch(batchId)} disabled={disabled} />
                </>
              );
            },
            padding: 'checkbox',
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'batchNo',
            displayName: Translation('opm_pd.batchNo'),
            renderData: (row) => {
              return (
                <Typography variant="inherit" noWrap>
                  {row.batchNo}
                </Typography>
              );
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'importedAt',
            displayName: Translation('opm_pd.importDate'),
            renderData: (row) => formatDateTime(row.importedAt, 'DD/MM/YYYY'),
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedAt',
            displayName: Translation('opm_pd.submittedDate'),
            renderData: (row) => formatDateTime(row.submittedAt, 'DD/MM/YYYY'),
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'cancelledAt',
            displayName: Translation('opm_pd.cancelledDate'),
            renderData: (row) => formatDateTime(row.cancelledAt, 'DD/MM/YYYY'),
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'importedPoliciesCount',
            displayName: Translation('opm_pd.importedPolicyCount'),
            renderData: (row) => formatNumber(row.importedPoliciesCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedPoliciesCount',
            displayName: Translation('opm_pd.submittedPolicies'),
            renderData: (row) => formatNumber(row.submittedPoliciesCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'awaitingApprovalCount',
            displayName: Translation('opm_pd.awaitingApproval'),
            renderData: (row) => formatNumber(row.awaitingApprovalCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'holdCount',
            displayName: Translation('opm_pd.hold'),
            renderData: (row) => formatNumber(row.holdCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'doneCount',
            displayName: 'Approved',
            renderData: (row) => formatNumber(row.doneCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'transferredCount',
            displayName: Translation('opm_pd.done'),
            renderData: (row) => formatNumber(row.transferredCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'endCount',
            displayName: Translation('opm_pd.rejectedEndCase'),
            renderData: (row) => formatNumber(row.endCount),
            align: 'right',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'batchStatus',
            displayName: Translation('opm_pd.status'),
            renderData: (row) => {
              const bdmBatchFileStatus = bdmBatchFileStatusMap[row.batchStatus];

              return (
                <Chip
                  color={(bdmBatchFileStatus?.color as any) || 'default'}
                  size="small"
                  label={bdmBatchFileStatus ? Translation(bdmBatchFileStatus.label) : ''}
                />
              );
            },
            align: 'center',
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'actions',
            displayName: Translation('opm_pd.operations'),
            renderData: (row) => {
              const canView = canViewEndedBdmBatchFilePolicy(row.batchStatus);
              const canSubmit = canSubmitBdmBatchFilePolicy(row.batchStatus);
              return (
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                  {canView && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      component={Link}
                      to={`/opm-pd/policy-transfer-request/batch-files/${row.id}/view`}
                    >
                      {Translation('opm_pd.view')}
                    </Button>
                  )}
                  {canSubmit && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      component={Link}
                      to={`/opm-pd/policy-transfer-request/batch-files/${row.id}/submit`}
                    >
                      {Translation('opm_pd.submit')}
                    </Button>
                  )}
                </Box>
              );
            },
            align: 'right',
            sortable: false,
          },
        ]}
        isLoading={isLoading}
        onRefresh={refreshData}
        dataSource={batchFilesList?.items || []}
        totalPages={batchFilesList?.totalPages}
        totalRecords={batchFilesList?.total}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({ ...filterState, page, limit: rowsPerPage });
          refreshData();
        }}
      />
    </BatchFileListContext.Provider>
  );
};

export default BatchFileList;
